"use client";

import {
  FC,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";

import { IStoryBlokComponent } from "apps/website/components/page/api.types";
import { useAuth } from "@auth/client-sdk-react";
import { CustomerAPI } from "@api-clients/customer";
import { PublicConfig } from "apps/website/config/public";
import {
  IMySubscriptions,
} from "libs/api/customer/src/lib/types/subscriptions";
import {
  checkHalloweenRecipeEligibility,
} from "apps/website/utils/website/halloween-recipe-eligibilty";
import { useAPI } from "apps/website/hooks/useAPI";

import SectionWarningCard from "../../section/SectionWarningCard/SectionWarningCard";
import SectionCheckboxEnabledCTA from "../../section/SectionCheckboxEnabledCTA/SectionCheckboxEnabledCTA";
import {
  TPrecancellationSubscriptionState,
} from "../../section/SectionSplash/SectionPreCancellationSplash/SectionPreCancellationSplash";

export interface IStoryBlokWarningCard {
  title: string;
  subscription_state?: TPrecancellationSubscriptionState;
}

interface IWarningBlock extends Omit<IStoryBlokWarningCard, "subscription_state"> {
  accessToken?: string;
  subscriptionId: string | null;
  subscriptionState?: TPrecancellationSubscriptionState;
}

const getDiscountText = (discountType: string, value: number): string => {
  if (discountType === "absolute_total") {
    return `£${value.toFixed(2)}`;
  }
  if (discountType === "percentage") {
    return `${value}%`;
  }
  return `£${value.toFixed(2)}`;
};

/**
 * Determines the appropriate warning card content based on user state
 */
const getWarningCardContent = (
  title: string,
  userSubscriptions: IMySubscriptions | undefined,
  subscriptionId: string | null,
  eligibleForHalloweenRecipe: boolean,
  eligibleForToteBag: boolean,
): { title: string; body: string } | null => {
  if (!subscriptionId || !userSubscriptions) {
    return null;
  }

  const hasDiscount = userSubscriptions.discountData?.appliesToSubGroups?.[subscriptionId];
  const discount = userSubscriptions.discountData?.discount;

  // Halloween recipe takes priority
  if (eligibleForHalloweenRecipe) {
    return {
      title: "Don't miss out on your spooky surprise!",
      body: "You have a FREE special-edition recipe in your next box delivered in October, and you'll lose it if you cancel.",
    };
  }

  // Discount + tote bag combination
  if (hasDiscount && eligibleForToteBag && discount) {
    const discountText = discount.discount_type === "absolute_total"
      ? `discounted price of £${discount.value.toFixed(2)}`
      : `${getDiscountText(discount.discount_type, discount.value)} off`;

    return {
      title,
      body: `You'll get your next box at a ${discountText} AND a surprise gift in your next box. You'll lose them if you cancel.`,
    };
  }

  // Tote bag only
  if (eligibleForToteBag && !hasDiscount) {
    return {
      title,
      body: "You have a surprise gift in your next box, and you'll lose it if you cancel.",
    };
  }

  // Discount only
  if (hasDiscount && !eligibleForHalloweenRecipe && discount) {
    const discountText = discount.discount_type === "absolute_total"
      ? `discounted price of £${discount.value.toFixed(2)}`
      : `${getDiscountText(discount.discount_type, discount.value)} off your next box`;

    return {
      title,
      body: `You'll get your next box at a ${discountText}, and you'll lose the offer if you cancel.`,
    };
  }

  return null;
};

const WarningBlock: FC<IWarningBlock> = ({ title, accessToken, subscriptionId }) => {

  const [ userSubscriptions, setUserSubscriptions ] = useState<IMySubscriptions>();
  const [ eligibleForHalloweenRecipe, setEligibleForHalloweenRecipe ] = useState<boolean>(false);
  const [ eligibleForToteBag, setEligibleForToteBag ] = useState<boolean>(false);
  const { Orders } = useAPI();

  useEffect(() => {
    if (accessToken) {
      const effectSetSubscriptionsAndHalloweenState = async () => {
        // useAPI with .Customer returns 401?
        try {
          const customer = new CustomerAPI(
            PublicConfig.NEXT_PUBLIC_OLD_API_BASE_URL,
            accessToken,
            { withCredentials: true },
          );
          const data = await customer.getAllSubscriptions();
          setUserSubscriptions(data);
          if (!subscriptionId) {
            return;
          }
          const onBoxTwo = data.freshMealPlans.find((x) => x.parentId)?.boxNumber === "1";
          setEligibleForToteBag(onBoxTwo);
          const isEligibleForHalloweenRecipe = await checkHalloweenRecipeEligibility(customer, Orders, +subscriptionId);
          setEligibleForHalloweenRecipe(isEligibleForHalloweenRecipe);
        } catch (error) {
          console.log("Error figuring out if to show warning card", error);
        }
      };
      void effectSetSubscriptionsAndHalloweenState();
    }
  }, [ accessToken, subscriptionId ]);

  const warningCardContent = useMemo(() => getWarningCardContent(
    title,
    userSubscriptions,
    subscriptionId,
    eligibleForHalloweenRecipe,
    eligibleForToteBag,
  ), [ title, userSubscriptions, subscriptionId, eligibleForHalloweenRecipe, eligibleForToteBag ]);

  return (
    <>
      { warningCardContent && (
        <SectionWarningCard
          title={warningCardContent.title}
          body={warningCardContent.body}
        />
      ) }
    </>
  );
};

const StoryBlokWarningCardChild: FC<IStoryBlokComponent<IStoryBlokWarningCard>> = ({ blok }) => {
  const { accessToken } = useAuth();
  const searchParams = useSearchParams();

  return (
    <>
      <WarningBlock title={blok.title} subscriptionState={blok.subscription_state} accessToken={accessToken} subscriptionId={searchParams.get("subscriptionId")} />
      <SectionCheckboxEnabledCTA></SectionCheckboxEnabledCTA>
    </>
  );
};

export const StoryBlokWarningCard: FC<IStoryBlokComponent<IStoryBlokWarningCard>> = ({ blok }) => (
  <Suspense>
    <StoryBlokWarningCardChild blok={blok} />
  </Suspense>
);
